import type { AddressInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import type { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';
import type { SocialsInterface } from '@valk-nx/core/lib/interfaces/socials.interface';
import type { RoomFacilities } from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import type {
	FilterableHotel,
	PackageBatchPriceInterface,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

const SOCIAL_KEYS: readonly (keyof SocialsInterface)[] = [
	'facebook',
	'instagram',
	'linkedin',
	'pinterest',
	'tiktok',
	'whatsapp',
	'youtube',
];

export function postalAddressSchemeOrg(address: AddressInterface) {
	return {
		'@type': 'PostalAddress' as const,
		streetAddress:
			`${address.street} ${address.streetNumber} ${address.streetNumberAddition || ''}`.trim(),
		addressLocality: address.city,
		postalCode: address.postalCode,
		addressCountry: address.countryCode || 'NL',
	};
}

export function brandSchemeOrg(brandId: string) {
	return {
		'@id': brandId,
		'@type': 'Organization' as const,
	};
}

export function parentOrganizationSchemeOrg(brandId: string) {
	return {
		'@type': ['Brand', 'Organization'] as const,
		'@id': brandId,
		name: 'Van der Valk',
		url: 'https://www.valk.com',
		sameAs: [
			'https://g.co/kgs/kJaxLh',
			'https://www.wikidata.org/wiki/Q2802214',
			'https://nl.wikipedia.org/wiki/Van_der_Valk_(bedrijf)',
			'https://www.instagram.com/valkverrast',
			'https://www.facebook.com/VanderValkVerrast',
			'https://www.youtube.com/user/HotelsVanDerValk',
			'https://www.tiktok.com/@valkverrast',
			'https://nl.pinterest.com/vandervalkhotels/',
		],
		foundingDate: '1929',
	};
}

export function contactPointSchemeOrg(telephone: string | undefined) {
	return {
		'@type': 'ContactPoint' as const,
		contactType: 'Reservations',
		telephone,
		hoursAvailable: {
			'@type': 'OpeningHoursSpecification' as const,
			opens: '00:00',
			closes: '23:59',
			dayOfWeek: [
				'Monday',
				'Tuesday',
				'Wednesday',
				'Thursday',
				'Friday',
				'Saturday',
				'Sunday',
			],
		},
	};
}

export function mainEntityOfPageSchemeOrg(
	url: string,
	name: string,
	publisherId: string,
	inLanguage?: string | null,
	id?: string,
) {
	return {
		'@type': 'ItemPage' as const,
		'@id': id || url,
		url,
		name,
		...(inLanguage && { inLanguage }),
		publisher: { '@id': publisherId },
	};
}

export function collectionPageSchemeOrg(
	url: string,
	name: string,
	publisherId: string,
	mentionsId: string,
	inLanguage?: string | null,
) {
	return {
		'@type': 'CollectionPage' as const,
		'@id': `${url}#LP`,
		url,
		name,
		publisher: { '@id': publisherId, '@type': 'Organization' },
		mentions: { '@id': mentionsId },
		...(inLanguage && { inLanguage }),
	};
}

export function quantitativeValueSchemeOrg(value: number, unitCode: string) {
	return {
		'@type': 'QuantitativeValue' as const,
		value,
		unitCode,
	};
}

export function hotelContainedInPlaceSchemeOrg(
	hotelId: string,
	name: string,
	url: string,
	address: AddressInterface,
	sameAs?: string,
) {
	return {
		'@type': 'Hotel' as const,
		'@id': hotelId,
		name,
		url,
		...(sameAs && { sameAs }),
		address: postalAddressSchemeOrg(address),
	};
}

export function imageSchemeOrg(
	images: PhotoInterface[],
	width: number,
	height: number,
	useArray = false,
) {
	if (images.length === 0) {
		return undefined;
	}

	const createImageObject = (image: PhotoInterface) => ({
		'@type': 'ImageObject' as const,
		url: image.src,
		name: image.alt,
		width: {
			'@type': 'QuantitativeValue' as const,
			value: width,
			unitCode: 'E37',
		},
		height: {
			'@type': 'QuantitativeValue' as const,
			value: height,
			unitCode: 'E37',
		},
	});

	return {
		image: useArray
			? images.map(createImageObject)
			: createImageObject(images[0]),
	};
}

export function amenitySchemeOrg(amenities: RoomFacilities) {
	const { sleepComfort, bathroom, roomConvenience, accessibility } =
		amenities;
	return {
		amenityFeature: {
			'@type': 'LocationFeatureSpecification',
			value: 'True',
			name: [
				...(sleepComfort || []),
				...(bathroom || []),
				...(roomConvenience || []),
				...(accessibility || []),
			],
		},
	};
}

export function businessFunctionSchemeOrg(
	functionType: 'Sell' | 'LeaseOut' = 'Sell',
) {
	return {
		businessFunction: {
			'@type': 'BusinessFunction' as const,
			url: `http://purl.org/goodrelations/v1#${functionType}`,
		},
	};
}

export function eligibleCustomerTypeSchemeOrg() {
	return {
		eligibleCustomerType: {
			'@type': 'BusinessEntityType' as const,
			url: 'http://purl.org/goodrelations/v1#Enduser',
		},
	};
}

interface PriceSpecificationSchemeOrg {
	priceSpecification: {
		'@type': 'UnitPriceSpecification';
		name?: string;
		price: number;
		minPrice: number;
		priceCurrency: 'EUR' | 'USD';
		unitCode?: 'DAY';
		referenceQuantity?: {
			'@type': 'QuantitativeValue';
			value: number;
			unitCode: 'IE' | 'C62';
		};
	};
}

export function priceSpecificationSchemeOrg(
	// PriceInterface is assignable to PackageBatchPriceInterface, so callers
	// with a regular price are accepted as well.
	price: PackageBatchPriceInterface,
	unitCode: 'DAY' | 'IE' | 'C62' = 'DAY',
	name?: string,
): PriceSpecificationSchemeOrg | undefined {
	// Omit the price specification entirely when no price is available.
	if (price.amount === null) {
		return undefined;
	}

	const amount = price.amount / 100;
	return {
		priceSpecification: {
			'@type': 'UnitPriceSpecification' as const,
			...(name && { name }),
			price: amount,
			minPrice: amount,
			priceCurrency: price.currency || 'EUR',
			...(unitCode === 'DAY'
				? { unitCode }
				: {
						referenceQuantity: {
							'@type': 'QuantitativeValue' as const,
							value: 1,
							unitCode,
						},
					}),
		},
	};
}

export function areaServedSchemeOrg(
	latitude?: number,
	longitude?: number,
	geoRadius = 0,
	placeName?: string,
	placeUrl?: string,
) {
	if (latitude === undefined || longitude === undefined) {
		return undefined;
	}

	return {
		areaServed:
			placeName && placeUrl
				? {
						'@type': 'Place' as const,
						name: placeName,
						url: placeUrl,
					}
				: {
						'@type': 'GeoCircle' as const,
						geoMidpoint: {
							'@type': 'GeoCoordinates' as const,
							latitude,
							longitude,
						},
						geoRadius,
					},
	};
}

export function acceptedPaymentMethodSchemeOrg() {
	return [
		{
			'@type': 'PaymentMethod' as const,
			'@id': 'http://purl.org/goodrelations/v1#ByBankTransferInAdvance',
		},
		{
			'@type': 'PaymentMethod' as const,
			'@id': 'http://purl.org/goodrelations/v1#Cash',
		},
		{
			'@type': 'PaymentMethod' as const,
			'@id': 'http://purl.org/goodrelations/v1#DirectDebit',
		},
		{
			'@type': 'PaymentMethod' as const,
			'@id': 'http://purl.org/goodrelations/v1#PayPal',
		},
	];
}

export function organizationSchemeOrg(name: string, url: string) {
	return {
		'@type': 'Organization' as const,
		name,
		url,
	};
}

export function socialProfilesSchemeOrg(
	socials: SocialsInterface | null,
): { sameAs: string[] } | undefined {
	if (!socials) {
		return undefined;
	}

	const socialProfileUrls = SOCIAL_KEYS.map((key) => socials[key]).filter(
		(value) => value !== null,
	);

	return socialProfileUrls.length > 0
		? { sameAs: socialProfileUrls }
		: undefined;
}

export function filterableHotelAddressSchemaOrg(
	address: FilterableHotel['address'],
) {
	const streetParts = [
		address.street,
		address.streetNumber,
		address.streetNumberAddition,
	].filter(Boolean);

	return {
		'@type': 'PostalAddress' as const,
		...(streetParts.length && { streetAddress: streetParts.join(' ') }),
		addressLocality: address.city,
		postalCode: address.postalCode,
		...(address.countryCode && {
			addressCountry: {
				'@type': 'Country' as const,
				name: address.countryCode,
			},
		}),
	};
}

export function filterableHotelRatingSchemaOrg(
	rating: FilterableHotel['rating'],
) {
	return {
		...(rating.stars && {
			starRating: {
				'@type': 'Rating' as const,
				ratingValue: rating.stars,
			},
		}),
		...(rating.numberOfRatings &&
			rating.score && {
				aggregateRating: {
					'@type': 'AggregateRating' as const,
					ratingValue: rating.score,
					reviewCount: rating.numberOfRatings,
					bestRating: 10,
				},
			}),
	};
}

export function offersSchemeOrg(price: PriceInterface, brand: string) {
	const amount = price.amount / 100;
	return {
		offers: {
			'@type': 'Offer' as const,
			offeredBy: { '@id': brand },
			seller: { '@id': brand },
			...businessFunctionSchemeOrg('LeaseOut'),
			...eligibleCustomerTypeSchemeOrg(),
			...priceSpecificationSchemeOrg(price, 'DAY'),
			price: amount,
			priceCurrency: price.currency || 'EUR',
			acceptedPaymentMethod: acceptedPaymentMethodSchemeOrg(),
		},
	};
}
